/**
 * Custom no-cap collection filter — Stage A: fetch the FULL collection
 * dataset (every product + every configured metafield) via the Storefront
 * API, no matter how many filterable attributes the store has (the
 * Search & Discovery app caps at 25 filters store-wide; this reads
 * whatever list is configured on each collection's `custom.filter_config`
 * metafield instead, with no such limit).
 *
 * Renders the sidebar using the theme's own filter markup/classes
 * (see snippets/collection-sidebar.liquid) so no CSS has to change, but
 * with FACETED (dependent) counts: each filter's option counts are
 * computed against products that already match every OTHER active
 * filter + the current price range, not the whole collection — so
 * picking "Bulbrite" then opening "Category" only shows categories that
 * actually have Bulbrite products, with everything else shown at (0)
 * and disabled, exactly like the native Search & Discovery sidebar does.
 *
 * Exposes window.SF_FULL_PRODUCTS / window.SF_FULL_PRODUCTS_LOADED for
 * collection-custom-controller.js, which owns all click/change handling
 * and actually renders the product grid.
 */
(function () {
  'use strict';

  if (window.SFC_facetsLoaded) return;
  window.SFC_facetsLoaded = true;

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // A metafield value can itself hold multiple comma-separated values on
  // one product (e.g. "Smart Downlights, LED Canless Recessed
  // Downlights") — split those into separate, trimmed, unique filter
  // options instead of treating the whole string as one option. A
  // product with N values here counts toward N separate facet options,
  // and matches a filter checked on ANY one of them (see matchesExcept
  // below and the equivalent split/match in collection-custom-controller.js
  // — keep both in sync, they must agree on what a product "has").
  function splitFieldValues(value, key) {
    if (value === undefined || value === null || value === '') return [];
    if (key === 'wattage') return [String(value)];
    if (key === 'cct_kelvin') return [String(value)];
    return String(value)
      .split(',')
      .map(function (v) { return v.trim(); })
      .filter(function (v) { return v !== ''; });
  }

  // "vendor:Select By Brand,category:Category,wattage:Wattage" -> keys + labels
  function parseConfig(configStr) {
    var pairs = configStr.split(',');
    var keys = [];
    var labels = {};
    pairs.forEach(function (pair) {
      var idx = pair.indexOf(':');
      if (idx === -1) return;
      var key = pair.slice(0, idx).trim();
      var label = pair.slice(idx + 1).trim();
      if (!key) return;
      keys.push(key);
      labels[key] = label;
    });
    return { keys: keys, labels: labels };
  }

  function mapSortKey(sortBy) {
    switch (sortBy) {
      case 'best-selling': return { sortKey: 'BEST_SELLING', reverse: false };
      case 'price-ascending': return { sortKey: 'PRICE', reverse: false };
      case 'price-descending': return { sortKey: 'PRICE', reverse: true };
      case 'title-ascending': return { sortKey: 'TITLE', reverse: false };
      case 'title-descending': return { sortKey: 'TITLE', reverse: true };
      case 'created-ascending': return { sortKey: 'CREATED', reverse: false };
      case 'created-descending': return { sortKey: 'CREATED', reverse: true };
      case 'manual': return { sortKey: 'MANUAL', reverse: false };
      default: return { sortKey: 'COLLECTION_DEFAULT', reverse: false };
    }
  }

  function buildQuery(metafieldKeys) {
    var identifiers = metafieldKeys
      .filter(function (k) { return k !== 'vendor'; })
      .map(function (k) { return '{namespace:"custom", key:"' + k + '"}'; });

    return (
      'query($handle:String!,$cursor:String,$sortKey:ProductCollectionSortKeys,$reverse:Boolean){' +
      '  collection(handle:$handle){' +
      '    products(first:250, after:$cursor, sortKey:$sortKey, reverse:$reverse){' +
      '      pageInfo{ hasNextPage endCursor }' +
      '      edges{ node{' +
      '        id handle vendor title availableForSale' +
      '        selectedOrFirstAvailableVariant{ sku }' +
      '        images(first:2){ edges{ node{ url altText width height } } }' +
      '        priceRange{ minVariantPrice{ amount } }' +
      '        compareAtPriceRange{ minVariantPrice{ amount } }' +
      (identifiers.length ? '        mf: metafields(identifiers:[' + identifiers.join(',') + ']){ key value }' : '') +
      '      } }' +
      '    }' +
      '  }' +
      '}'
    );
  }

  // Vendor page (/collections/vendors?q=Halco) has no real collection to
  // query, so the dataset comes from a top-level products(query:"vendor:...").
  function getVendorName(root) {
    if (!root || root.getAttribute('data-vendor-mode') !== 'true') return '';
    var m = window.location.search.match(/[?&]q=([^&]*)/);
    if (!m) return '';
    try { return decodeURIComponent(m[1].replace(/\+/g, ' ')).trim(); } catch (e) { return ''; }
  }

  function buildVendorQuery(metafieldKeys) {
    var identifiers = metafieldKeys
      .filter(function (k) { return k !== 'vendor'; })
      .map(function (k) { return '{namespace:"custom", key:"' + k + '"}'; });

    return (
      'query($q:String!,$cursor:String,$sortKey:ProductSortKeys,$reverse:Boolean){' +
      '  products(first:250, query:$q, after:$cursor, sortKey:$sortKey, reverse:$reverse){' +
      '    pageInfo{ hasNextPage endCursor }' +
      '    edges{ node{' +
      '      id handle vendor title availableForSale' +
      '      selectedOrFirstAvailableVariant{ sku }' +
      '      images(first:2){ edges{ node{ url altText width height } } }' +
      '      priceRange{ minVariantPrice{ amount } }' +
      '      compareAtPriceRange{ minVariantPrice{ amount } }' +
      (identifiers.length ? '      mf: metafields(identifiers:[' + identifiers.join(',') + ']){ key value }' : '') +
      '    } }' +
      '  }' +
      '}'
    );
  }

  function vendorSortInfo(sortInfo) {
    var k = sortInfo.sortKey;
    if (k === 'CREATED') k = 'CREATED_AT';
    if (k === 'COLLECTION_DEFAULT' || k === 'MANUAL') k = 'RELEVANCE';
    return { sortKey: k, reverse: sortInfo.reverse };
  }

  var PAGE_FETCH_MAX_ATTEMPTS = 3;
  var PAGE_FETCH_RETRY_DELAY_MS = 600;

  function delay(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  function fetchAllProducts(endpoint, token, handle, query, sortInfo, vendorName) {
    var all = [];

    function fetchPageOnce(cursor) {
      var variables = vendorName
        ? { q: 'vendor:"' + vendorName.replace(/(["\\])/g, '\\$1') + '"', cursor: cursor || null, sortKey: vendorSortInfo(sortInfo).sortKey, reverse: sortInfo.reverse }
        : { handle: handle, cursor: cursor || null, sortKey: sortInfo.sortKey, reverse: sortInfo.reverse };
      return fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token
        },
        body: JSON.stringify({
          query: query,
          variables: variables
        })
      })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          if (vendorName) {
            if (!res.data || !res.data.products) {
              throw new Error('Storefront API returned no vendor product data: ' + JSON.stringify(res.errors || res));
            }
            return res.data.products;
          }
          if (!res.data || !res.data.collection) {
            throw new Error('Storefront API returned no collection data: ' + JSON.stringify(res.errors || res));
          }
          return res.data.collection.products;
        });
    }

    function fetchPageWithRetry(cursor, attempt) {
      return fetchPageOnce(cursor).catch(function (err) {
        var nextAttempt = (attempt || 1) + 1;
        if (nextAttempt > PAGE_FETCH_MAX_ATTEMPTS) throw err;
        if (window.console) console.warn('Facet page fetch failed, retrying (' + nextAttempt + '/' + PAGE_FETCH_MAX_ATTEMPTS + ')', err);
        return delay(PAGE_FETCH_RETRY_DELAY_MS).then(function () { return fetchPageWithRetry(cursor, nextAttempt); });
      });
    }

    function fetchPage(cursor) {
      return fetchPageWithRetry(cursor, 1).then(function (conn) {
        conn.edges.forEach(function (e) {
          var node = e.node;
          var mf = {};
          (node.mf || []).forEach(function (m) { if (m) mf[m.key] = m.value; });
          var compareAt = node.compareAtPriceRange && node.compareAtPriceRange.minVariantPrice
            ? parseFloat(node.compareAtPriceRange.minVariantPrice.amount)
            : null;
          var price = parseFloat(node.priceRange.minVariantPrice.amount);
          var images = (node.images && node.images.edges || []).map(function (ie) { return ie.node; });
          all.push({
            id: node.id,
            handle: node.handle,
            vendor: node.vendor,
            sku: node.selectedOrFirstAvailableVariant && node.selectedOrFirstAvailableVariant.sku || '',
            title: node.title,
            available: node.availableForSale,
            image: images[0] || null,
            hoverImage: images[1] || null,
            price: price,
            compareAtPrice: compareAt && compareAt > price ? compareAt : null,
            mf: mf
          });
        });
        if (conn.pageInfo.hasNextPage) return fetchPage(conn.pageInfo.endCursor);
      });
    }

    return fetchPage(null).then(function () { return all; });
  }

  // Faceted (dependent) matching: does a product satisfy every active
  // filter EXCEPT excludeKey, plus the price range? Used to compute each
  // filter group's own option counts against "everything else currently
  // selected", not the whole collection — this is what makes checking
  // one filter grey out/zero-count options in other filters that no
  // longer apply.
  function matchesExcept(product, activeFilters, priceMin, priceMax, excludeKey) {
    for (var key in activeFilters) {
      if (key === excludeKey) continue;
      var set = activeFilters[key];
      if (!set || !set.size) continue;
      var value = key === 'vendor' ? product.vendor : product.mf[key];
      var values = splitFieldValues(value, key);
      if (!values.length) return false;
      if (!values.some(function (v) { return set.has(v); })) return false;
    }
    if (priceMin !== null && !isNaN(priceMin) && product.price < priceMin) return false;
    if (priceMax !== null && !isNaN(priceMax) && product.price > priceMax) return false;
    return true;
  }

  function computeFacets(products, keys, activeFilters, priceMin, priceMax) {
    var facets = {}; // key -> { value -> count }
    keys.forEach(function (k) { facets[k] = {}; });

    // Seed every value that exists ANYWHERE in the full collection at 0
    // first. Without this, a value with zero matches under the current
    // filters never gets an entry at all (the increment loop below only
    // ever touches values that DO match something) -- renderFacetGroup
    // then has no way to know it should render that option
    // disabled/greyed-out, since Object.keys() simply never sees it. The
    // option would silently disappear from the list instead of showing
    // as unselectable, which is the wrong UX (looks like the filter
    // doesn't exist rather than "not compatible with your other picks").
    keys.forEach(function (key) {
      products.forEach(function (p) {
        var val = key === 'vendor' ? p.vendor : p.mf[key];
        splitFieldValues(val, key).forEach(function (strVal) {
          if (!(strVal in facets[key])) facets[key][strVal] = 0;
        });
      });
    });

    products.forEach(function (p) {
      keys.forEach(function (key) {
        if (!matchesExcept(p, activeFilters, priceMin, priceMax, key)) return;
        var val = key === 'vendor' ? p.vendor : p.mf[key];
        splitFieldValues(val, key).forEach(function (strVal) {
          facets[key][strVal] = (facets[key][strVal] || 0) + 1;
        });
      });
    });

    return facets;
  }

  function fullPriceRange(products) {
    var min = null, max = null;
    products.forEach(function (p) {
      if (min === null || p.price < min) min = p.price;
      if (max === null || p.price > max) max = p.price;
    });
    return { min: min || 0, max: max || 0 };
  }

  // Per-filter-key unit suffix, appended to a value's display text only
  // when it isn't already there (case-insensitive) — e.g. wattage "10.0"
  // -> "10.0W", but "10.0W" (if a product already has it) stays as-is,
  // and CCT "3000K" is left alone since it already ends in K.
  var UNIT_SUFFIXES = { wattage: 'W', cct_kelvin: 'K', voltage: 'V', equivalent_wattage: 'W' };

  function displayValue(val, key) {
    var lower = String(val).toLowerCase();
    if (lower === 'true') return 'Yes';
    if (lower === 'false') return 'No';
    var suffix = key && UNIT_SUFFIXES[key];
    if (suffix) {
      return String(val).trim().split('/').map(function (part) {
        var p = part.trim();
        return /^\d+(\.\d+)?$/.test(p) ? p + suffix : p;
      }).join('/');
    }
    return val;
  }

  function renderPriceBlock(priceMin, priceMax, curMin, curMax, isOpen) {
    priceMin = Math.round(priceMin * 100) / 100;
    priceMax = Math.round(priceMax * 100) / 100;
    curMin = curMin === null || isNaN(curMin) ? priceMin : curMin;
    curMax = curMax === null || isNaN(curMax) ? priceMax : curMax;
    return (
      '<div class="filter-item px-30 px-1025-0"><collapsible-block data-facet-key="price" class="collection border-bottom block pb-5' + (isOpen ? ' active' : '') + '">' +
      '<h3 class="collapsible-heading w-full relative my-0 heading-letter-spacing h6 pt-15 pb-10"><span class="inline-flex gap-10 align-center lh-normal">Price</span>' +
      '<span class="open-children-toggle absolute inset-0 flex flex-end pointer"><span class="icon_plus-animation"> </span></span></h3>' +
      '<div class="collapsible-content filter-content custom-scrollbar pb-20">' +
      '<form class="filter-form" onsubmit="return false">' +
      '<div class="filter-price"><price-range class="price-range flex gap-10 align-center">' +
      '<div class="field flex-1 relative"><span class="field-currency absolute lh-normal left-15 inset-y-0 inline-flex align-center">$</span>' +
      '<input id="sf-price-min" class="min-price-range w-full rounded-3 text-size" style="--input-height: 4rem; --input-padding: 1rem 2rem 1rem 4rem" type="text" min="' + priceMin + '" max="' + priceMax + '" value="' + curMin + '"></div>' +
      '<svg width="10" height="2" viewBox="0 0 10 2" fill="none"><path d="M10 0.5L10 1.5L-4.37114e-08 1.5L0 0.5L10 0.5Z" fill="#666" /></svg>' +
      '<div class="field flex-1 relative"><span class="field-currency absolute lh-normal left-15 inset-y-0 inline-flex align-center">$</span>' +
      '<input id="sf-price-max" class="max-price-range w-full rounded-3" style="--input-height: 4rem; --input-padding: 1rem 2rem 1rem 4rem" type="text" min="' + priceMin + '" max="' + priceMax + '" value="' + curMax + '"></div>' +
      '</price-range></div>' +
      '<div class="price-range mt-25 mb-20 drop-range relative" id="sf-price-track" style="--range-from: 0%; --range-to: 100%">' +
      '<label for="sf-range-slider-min" class="visually-hidden">Range</label>' +
      '<input type="range" class="range-slider range-slider-min" id="sf-range-slider-min" min="' + priceMin + '" max="' + priceMax + '" step="0.01" value="' + curMin + '">' +
      '<label for="sf-range-slider-max" class="visually-hidden">Range</label>' +
      '<input type="range" class="range-slider range-slider-max absolute right-0 top-0" id="sf-range-slider-max" min="' + priceMin + '" max="' + priceMax + '" step="0.01" value="' + curMax + '">' +
      '</div>' +
      '<div class="price-lable flex gap-5 flex-wrap"><div class="label">Price:</div><div class="price heading-style">' +
      '<span class="min" id="sf-price-label-min">$' + curMin + '</span> - <span class="max" id="sf-price-label-max">$' + curMax + '</span></div></div>' +
      '</form></div></collapsible-block></div>'
    );
  }

  // Plain alphabetic sort puts "10800" before "110" (string comparison).
  // When every value in a group is actually numeric (Wattage, Lumens,
  // CCT, Voltage, etc. — a plain number, optionally with a trailing unit
  // letter like "3000K"), sort by numeric value instead so options read
  // lowest-to-highest. Falls back to normal alphabetic sort for anything
  // non-numeric (Category, Brand, ...).
  function sortValues(values) {
    var allNumeric = values.every(function (v) {
      return v !== '' && !isNaN(parseFloat(v));
    });
    if (allNumeric) {
      return values.slice().sort(function (a, b) { return parseFloat(a) - parseFloat(b); });
    }
    return values.slice().sort();
  }

  function renderFacetGroup(key, label, valueCounts, activeSet, isColor, isSize, isOpen) {
    var values = sortValues(Object.keys(valueCounts));
    var activeCount = activeSet ? activeSet.size : 0;
    // A value only actually renders below if it has results or is
    // already checked (see the `if (count === 0 && !checked) return;`
    // guards further down) — if every value in this group would be
    // hidden that way, the whole group (heading included) should
    // disappear too, rather than showing an empty expanded section
    // with no options in it.
    var hasVisibleValue = values.some(function (val) {
      return valueCounts[val] > 0 || (activeSet && activeSet.has(val));
    });
    if (!hasVisibleValue) return '';
    var html = '<div class="filter-item px-30 px-1025-0"><collapsible-block data-facet-key="' + escapeHtml(key) + '" class="collection border-bottom block pb-5' + (isOpen ? ' active' : '') + '">';
    html +=
      '<h3 class="collapsible-heading w-full relative my-0 heading-letter-spacing h6 pt-15 pb-10"><span class="inline-flex gap-10 align-center lh-normal">' +
      escapeHtml(label) +
      (activeCount > 0 ? '<span class="total_filter rounded-50 grey-bg w-20 h-20 inline-flex content-center fs-10">' + activeCount + '</span>' : '') +
      '</span><span class="open-children-toggle absolute inset-0 flex flex-end pointer"><span class="icon_plus-animation"> </span></span></h3>';
    html += '<div class="collapsible-content filter-content custom-scrollbar pb-20">';

    if (isColor || isSize) {
      html += '<div class="swatch-attribute' + (isColor ? ' color' : ' size') + ' swatch-option custom-scrollbar"><ul class="items list-none p-0 m-0 flex flex-wrap gap-custom">';
      values.forEach(function (val) {
        var count = valueCounts[val];
        var checked = activeSet && activeSet.has(val);
        // Hide options that would return zero results together with the
        // current selection, rather than showing them disabled/greyed —
        // per client request (reverting an earlier "show disabled" change).
        if (count === 0 && !checked) return;
        html +=
          '<li class="item last-0 mb-0"><label class="link-filter">' +
          '<input type="checkbox" class="sf-filter-checkbox visually-hidden" data-key="' + escapeHtml(key) + '" value="' + escapeHtml(val) + '"' + (checked ? ' checked' : '') + '>' +
          '<div class="tooltip swatch-option pointer' + (isColor ? ' color relative' : ' size rounded-3 border px-15 py-10 lh-1 text-center btn-hover transition') + (checked ? ' current-filter' : '') + '">' +
          (isColor
            ? '<span class="product__color-swatches--js clrbox product__color-swatch relative block rounded-50" style="background-color:' + escapeHtml(val.toLowerCase()) + ';"></span>'
            : '<span class="relative z-2">' + escapeHtml(displayValue(val, key)) + '</span>') +
          '</div></label></li>';
      });
      html += '</ul></div>';
    } else {
      html += '<ul class="items list-none p-0 m-0 custom-scrollbar">';
      values.forEach(function (val) {
        var count = valueCounts[val];
        var checked = activeSet && activeSet.has(val);
        // Hide options that would return zero results together with the
        // current selection, rather than showing them disabled/greyed —
        // per client request (reverting an earlier "show disabled" change).
        if (count === 0 && !checked) return;
        html +=
          '<li class="item mb-10 last-0' + (checked ? ' current-filter' : '') + '">' +
          '<label class="link-filter pointer inline-flex lh-normal gap-10 hover-heading-color transition">' +
          '<input type="checkbox" class="sf-filter-checkbox visually-hidden" data-key="' + escapeHtml(key) + '" value="' + escapeHtml(val) + '"' + (checked ? ' checked' : '') + '>' +
          '<span class="checkbox border rounded-2 bg-white inline-flex content-center transition relative' + (checked ? ' checked' : '') + '">' +
          '<svg width="10" height="7" fill="none" class="' + (checked ? '' : 'invisible ') + 'absolute inset-0 z-0 m-auto"><path fill="currentColor" d="M8.645.645a.503.503 0 0 1 .351-.141.496.496 0 0 1 .504.504c0 .133-.05.246-.152.34L3.852 6.855a.477.477 0 0 1-.352.141.477.477 0 0 1-.352-.14L.652 4.347a.446.446 0 0 1-.152-.34c0-.14.05-.262.152-.363a.463.463 0 0 1 .34-.141c.14 0 .262.047.363.14L3.5 5.79 8.645.645Z"/></svg>' +
          '</span>' + escapeHtml(displayValue(val, key)) + ' (' + count + ')' +
          '</label></li>';
      });
      html += '</ul>';
    }
    html += '</div></collapsible-block></div>';
    return html;
  }

  var CACHE_TTL_MS = 15 * 60 * 1000;
  function cacheKey(handle, sortBy) { return 'sfcFullProducts:' + handle + ':' + (sortBy || ''); }
  function getCached(handle, sortBy) {
    try {
      var raw = sessionStorage.getItem(cacheKey(handle, sortBy));
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.savedAt || Date.now() - parsed.savedAt > CACHE_TTL_MS) return null;
      return parsed.products;
    } catch (e) { return null; }
  }
  function setCached(handle, sortBy, products) {
    try { sessionStorage.setItem(cacheKey(handle, sortBy), JSON.stringify({ savedAt: Date.now(), products: products })); }
    catch (e) { /* storage full/unavailable — skip caching */ }
  }

  function colorSizeKeys(config) {
    var colorLabels = (window.SFC_colorSwatchTrigger || 'Color').split(',').map(function (s) { return s.trim(); });
    var sizeLabels = (window.SFC_sizeSwatchTrigger || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    var colorKeys = {}, sizeKeys = {};
    config.keys.forEach(function (k) {
      if (colorLabels.indexOf(config.labels[k]) !== -1) colorKeys[k] = true;
      if (sizeLabels.indexOf(config.labels[k]) !== -1) sizeKeys[k] = true;
    });
    return { colorKeys: colorKeys, sizeKeys: sizeKeys };
  }

  // Rebuilding the facet list (container.innerHTML) on every filter/price
  // change would otherwise reset every accordion group back to collapsed,
  // even the one the visitor is actively using -- e.g. dragging the price
  // slider used to close the Price group on every tick. Whatever's
  // currently expanded is read straight from the live DOM right before
  // it's replaced, merged with whichever group the caller says was just
  // interacted with (so picking a brand checkbox opens/keeps open
  // "Select By Brand" even the very first time), and reapplied to the
  // freshly-rendered markup.
  function currentlyOpenKeys(container) {
    var open = {};
    container.querySelectorAll('collapsible-block.active[data-facet-key]').forEach(function (el) {
      open[el.getAttribute('data-facet-key')] = true;
    });
    return open;
  }

  function render(container, products, config, activeFilters, priceMin, priceMax, justChangedKey) {
    var openKeys = currentlyOpenKeys(container);
    if (justChangedKey) openKeys[justChangedKey] = true;

    var range = fullPriceRange(products);
    var facets = computeFacets(products, config.keys, activeFilters, priceMin, priceMax);
    var swatchTypes = colorSizeKeys(config);

    var html = renderPriceBlock(range.min, range.max, priceMin, priceMax, !!openKeys.price);
    config.keys.forEach(function (key) {
      html += renderFacetGroup(
        key,
        config.labels[key],
        facets[key],
        activeFilters[key],
        !!swatchTypes.colorKeys[key],
        !!swatchTypes.sizeKeys[key],
        !!openKeys[key]
      );
    });
    // Rebuilding the whole sidebar (innerHTML) on every single price
    // keystroke/drag tick destroys and recreates #sf-price-min/-max and the
    // range sliders each time -- that drops focus + caret mid-type (typing
    // "14" only ever kept the "1") and makes dragging look stepped since a
    // full facet-count recompute + DOM rebuild runs on every tick. While the
    // visitor is actively in one of those controls, leave that existing
    // Price node alone and only refresh the other filter groups around it;
    // collection-custom-controller.js's syncPriceVisuals() already updates
    // the slider track/labels live on every tick without needing this to
    // touch the DOM at all.
    var active = document.activeElement;
    var priceNode = null;
    if (active && container.contains(active) && /^sf-(price|range-slider)/.test(active.id)) {
      var priceBlock = container.querySelector('collapsible-block[data-facet-key="price"]');
      priceNode = priceBlock ? priceBlock.closest('.filter-item') : null;
    }
    if (priceNode) {
      var tmp = document.createElement('div');
      tmp.innerHTML = html;
      var rest = Array.prototype.slice.call(tmp.children).slice(1);
      Array.prototype.slice.call(container.children).forEach(function (c) {
        if (c !== priceNode) container.removeChild(c);
      });
      rest.forEach(function (n) { container.appendChild(n); });
    } else {
      container.innerHTML = html;
    }

    // theme.js's CollapsibleBlock custom element force-opens every
    // collapsible-block.collection (active class + display:block) on mobile
    // the instant it's upgraded (i.e. synchronously, as soon as this
    // innerHTML assignment inserts the <collapsible-block> tags) -- that's
    // stock behavior shared with the native filter sidebar. Undo it here,
    // scoped to just this custom filter's groups, right after insertion so
    // it still reflects whatever the visitor already had open (openKeys)
    // instead of forcing everything open by default.
    if (window.innerWidth <= 1024) {
      container.querySelectorAll('collapsible-block.collection[data-facet-key]').forEach(function (el) {
        var key = el.getAttribute('data-facet-key');
        if (openKeys[key]) return;
        el.classList.remove('active');
        var content = el.querySelector('.collapsible-content');
        if (content) content.style.display = 'none';
      });
    }
  }

  function init() {
    var root = document.getElementById('CollectionSidebar');
    if (!root) return;
    var configStr = root.getAttribute('data-cf-full-config');
    var endpoint = root.getAttribute('data-storefront-endpoint');
    var token = root.getAttribute('data-storefront-token');
    var handle = root.getAttribute('data-collection-handle');
    var sortBy = root.getAttribute('data-sort-by') || '';
    var vendorName = getVendorName(root);
    if (root.getAttribute('data-vendor-mode') === 'true') {
      if (!vendorName) return;
      handle = 'vendors:' + vendorName;
    }
    if (!configStr || !endpoint || !token || !handle) return;

    var container = root.querySelector('.filter-blocks');
    if (!container) return;

    var config = parseConfig(configStr);
    var sortInfo = mapSortKey(sortBy);

    // Always reads window.SF_FULL_PRODUCTS live (not a closed-over
    // "products" reference) so it can never render against a dataset
    // older than whatever computeMatches() in collection-custom-controller.js
    // is about to filter against for the grid -- both window.SFC_fetchForSort
    // (sort changes) and the background "stay fresh" re-fetch below
    // reassign window.SF_FULL_PRODUCTS directly without necessarily
    // happening to run before this closes over a stale snapshot, which
    // previously let the sidebar's facet counts and the grid's actual
    // matched total disagree.
    window.SFC_renderSidebar = function (activeFilters, priceMin, priceMax, justChangedKey) {
      render(container, window.SF_FULL_PRODUCTS || [], config, activeFilters, priceMin, priceMax, justChangedKey);
    };

    function finish(products) {
      window.SF_FULL_PRODUCTS = products;
      window.SF_FULL_PRODUCTS_LOADED = true;
      window.SFC_config = config;
      document.dispatchEvent(new CustomEvent('sfc:facets-ready', { detail: { total: products.length } }));
    }

    var cached = getCached(handle, sortBy);
    if (cached) {
      finish(cached);
    }

    var query = vendorName ? buildVendorQuery(config.keys) : buildQuery(config.keys);
    fetchAllProducts(endpoint, token, handle, query, sortInfo, vendorName)
      .then(function (products) {
        if (cached && cached.length > 0 && products.length < cached.length * 0.9) {
          if (window.console) console.warn('Skipping facet refresh: fetched ' + products.length + ', well below cached ' + cached.length);
          return;
        }
        setCached(handle, sortBy, products);
        finish(products);
      })
      .catch(function (err) {
        if (window.console) console.error('Full-collection facet fetch failed', err);
        if (!cached) container.innerHTML = '<p class="p-30">Unable to load filters right now.</p>';
      });
  }

  window.SFC_fetchForSort = function (sortBy) {
    var root = document.getElementById('CollectionSidebar');
    if (!root) return Promise.reject(new Error('no sidebar'));
    var configStr = root.getAttribute('data-cf-full-config');
    var endpoint = root.getAttribute('data-storefront-endpoint');
    var token = root.getAttribute('data-storefront-token');
    var handle = root.getAttribute('data-collection-handle');
    var vendorName = getVendorName(root);
    if (vendorName) handle = 'vendors:' + vendorName;
    var config = parseConfig(configStr);
    var cached = getCached(handle, sortBy);
    if (cached) {
      window.SF_FULL_PRODUCTS = cached;
      return Promise.resolve(cached);
    }
    var query = vendorName ? buildVendorQuery(config.keys) : buildQuery(config.keys);
    return fetchAllProducts(endpoint, token, handle, query, mapSortKey(sortBy), vendorName).then(function (products) {
      setCached(handle, sortBy, products);
      window.SF_FULL_PRODUCTS = products;
      return products;
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
